// Free trials from a provider's QR code: the provider makes codes, a learner
// opens /t/<code> and starts a trial of a set number of minutes.
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/AuthProvider';
import { rpc } from '@/lib/rpc';
import { siteOrigin } from '@/lib/site';

/** Why a code can't be used right now. */
export type TrialProblem = 'disabled' | 'expired' | 'unavailable' | 'full';

export interface TrialCode {
  id: string;
  code: string;
  label: string | null;
  minutes: number;
  expires_at: string | null;
  max_claims: number | null;
  status: 'active' | 'disabled';
  created_at: string;
  disabled_by_admin: boolean;
  claims: number;
  /** Of the people who started a trial: answered a question afterwards, and bought it (or a bundle with it) afterwards. */
  studied: number;
  purchased: number;
  problem: TrialProblem | null;
}

export interface TrialOffer {
  product_id: string;
  product_title: string;
  product_slug: string;
  product_subtitle: string | null;
  image_url: string | null;
  question_bank_id: string | null;
  provider_name: string | null;
  /** Length of the trial, already capped at the current maximum. */
  minutes: number;
  problem: TrialProblem | null;
  /** Signed in: they've had a trial of this product, or already have access. */
  my_status: 'used_trial' | 'has_access' | null;
  my_trial_ends_at: string | null;
}

export interface StartedTrial {
  product_id: string;
  product_slug: string;
  question_bank_id: string | null;
  minutes: number;
  expires_at: string;
}

export interface MyTrial {
  product_id: string;
  product_title: string;
  product_slug: string;
  question_bank_ids: string[];
  claimed_at: string;
  expires_at: string | null;
  active: boolean;
}

export interface TrialOverview {
  max_minutes: number;
  codes: {
    id: string;
    code: string;
    label: string | null;
    minutes: number;
    expires_at: string | null;
    max_claims: number | null;
    status: 'active' | 'disabled';
    created_at: string;
    product: string;
    provider: string | null;
    claims: number;
    claims_30_days: number;
    studied: number;
    purchased: number;
    problem: TrialProblem | null;
  }[];
}

export const trialKeys = {
  codes: (productId: string) => ['trial-codes', productId] as const,
  offer: (code: string) => ['trial-offer', code] as const,
  mine: ['my-trials'] as const,
  admin: ['admin-trials'] as const,
};

/** The link a QR code opens. Always the public web address, also from the app. */
export const trialUrl = (code: string) => `${siteOrigin()}/t/${code}`;

export const PROBLEM_TEXT: Record<TrialProblem, string> = {
  disabled: 'This trial is no longer available.',
  expired: 'This trial offer has ended.',
  unavailable: "This product isn't available right now.",
  full: 'This trial has been used by as many people as it allows.',
};

/** 20 → "20 minutes", 90 → "1 hour 30 minutes", 1440 → "1 day". */
/** "3 studied · 1 bought" once anyone has started a trial. */
export function trialResultsText(c: { claims: number; studied: number; purchased: number }): string {
  return c.claims > 0 ? ` · ${c.studied} studied · ${c.purchased} bought` : '';
}

export function trialLengthText(minutes: number): string {
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  const part = (n: number, unit: string) => (n ? `${n} ${unit}${n === 1 ? '' : 's'}` : '');
  return [part(d, 'day'), part(h, 'hour'), part(m, 'minute')].filter(Boolean).join(' ') || '0 minutes';
}

/** Time left as "1:05:09", "12:04" or "0:59". */
export function countdownText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

export const trialService = {
  create: (productId: string, minutes: number, label: string, expiresAt: string | null, maxClaims: number | null) =>
    rpc<{ id: string; code: string }>('create_trial_code', {
      _product_id: productId,
      _minutes: minutes,
      _label: label.trim() || null,
      _expires_at: expiresAt,
      _max_claims: maxClaims,
    }),
  list: (productId: string) => rpc<{ max_minutes: number; codes: TrialCode[] }>('my_trial_codes', { _product_id: productId }),
  disable: (codeId: string) => rpc<void>('disable_trial_code', { _code_id: codeId }),
  offer: (code: string) => rpc<TrialOffer | null>('get_trial_code', { _code: code }),
  start: (code: string) => rpc<StartedTrial>('claim_trial_code', { _code: code }),
  mine: () => rpc<MyTrial[]>('my_trials'),
  adminOverview: () => rpc<TrialOverview>('admin_trial_overview'),
};

/** The signed-in learner's trials. Refreshed often enough to notice one ending. */
export function useMyTrials() {
  const { user } = useAuth();
  return useQuery({ queryKey: trialKeys.mine, queryFn: trialService.mine, enabled: !!user, staleTime: 60_000 });
}
